import React, { useEffect, useState } from 'react';
import { Modal, Btn, Textarea, Select, Badge, RNLLoader, Spinner } from '../UI';
import { fmt, fmtDate } from '../../utils';
import * as api from '../../api';
import { pvStatusColor, pvStatusLabel, parsePvErrorMessage, reviewStatusColor, PAYMENT_TYPE_OPTIONS } from './pvUtils';
import VoucherOutcomeModal from './VoucherOutcomeModal';

const OUTCOME_MESSAGES = {
  approve: 'The voucher has been approved and moved to the next step in the workflow.',
  reject: 'The voucher has been rejected. This is final and cannot be resubmitted.',
  return: 'The voucher has been returned to the requester for correction.',
  hold: 'The voucher has been put on hold. It stays with you and can be actioned again later.',
};

const ACTION_META = {
  approve: { label: 'Approve', title: 'Approve Voucher', color: '#16a34a', verb: 'approve', commentRequired: false, placeholder: 'Optional comment...' },
  return: { label: 'Return', title: 'Return Voucher', color: '#f97316', verb: 'return', commentRequired: true, placeholder: 'Explain why this voucher is being returned...' },
  reject: { label: 'Reject', title: 'Reject Voucher', color: '#ef4444', verb: 'reject', commentRequired: true, placeholder: 'Explain why this voucher is being rejected...' },
  hold: { label: 'Hold', title: 'Put Voucher On Hold', color: '#eab308', verb: 'put on hold', commentRequired: true, placeholder: 'Explain why this voucher is being put on hold...' },
};

export default function VoucherDetailView({ voucherId, onBack, toast, showWorkflowActions, onActed, currentUser, showPostAction, onResubmit }) {
  const [voucher, setVoucher] = useState(null);
  const [tracking, setTracking] = useState(null);
  // The register's expand detail. It carries the SOURCE REQUISITION —
  // who raised it, for which department and project, and who approved it —
  // none of which is on the voucher payload, which knows only the PR
  // number. Supplementary, so a failure here never costs the page.
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  // Which action is resolving its next step, before its dialog opens.
  const [preparing, setPreparing] = useState(null);
  const [resubmitting, setResubmitting] = useState(false);
  const [showResubmit, setShowResubmit] = useState(false);
  const [resubmitComment, setResubmitComment] = useState('');
  const [comment, setComment] = useState('');
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [nextStepInfo, setNextStepInfo] = useState(null);
  const [nextApproverId, setNextApproverId] = useState('');
  const [outcome, setOutcome] = useState(null);
  const [showPostConfirm, setShowPostConfirm] = useState(false);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    api.markEntitySeen(voucherId, 'payment_voucher').catch(() => {});
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const v = await api.getPaymentVoucher(voucherId);
        if (cancelled) return;
        setVoucher(v);
        if (v.workflow_instance_id) {
          const t = await api.getWorkflowTracking(v.workflow_instance_id);
          if (!cancelled) setTracking(t);
        }
        try {
          const d = await api.getPostedVoucherRowDetail(voucherId);
          if (!cancelled) setSource(d?.requisition || null);
        } catch { /* the voucher stands on its own without it */ }
      } catch (e) {
        if (!cancelled) toast.show(e.message, 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherId]);

  const openActionModal = async (type) => {
    setComment('');
    setNextStepInfo(null);
    setNextApproverId('');

    // The next step is resolved BEFORE the dialog opens. Opening first and
    // filling in afterwards produced a modal whose approver field arrived a
    // beat late while Confirm Approve was already clickable — on a slow
    // connection, an approval sent with no next approver chosen.
    if (type === 'approve' && voucher?.workflow_instance_id) {
      setPreparing(type);
      try {
        const details = await api.getNextStepDetails(voucher.workflow_instance_id);
        setNextStepInfo(details);
        if (details?.approver_mode === 'specific_user' && details?.eligible_users?.length > 0) {
          setNextApproverId(details.eligible_users[0].user_id || details.eligible_users[0].id);
        }
      } catch (e) {
        toast.show(`Could not load the next approval step: ${e.message}`, 'error');
        setPreparing(null);
        return;   // never open a half-built dialog
      }
      setPreparing(null);
    }
    setActionType(type);
    setShowActionModal(true);
  };

  const submitAction = async () => {
    if (ACTION_META[actionType].commentRequired && !comment.trim()) {
      toast.show(`A comment is required to ${ACTION_META[actionType].verb} this voucher.`, 'error');
      return;
    }
    if (actionType === 'approve' && nextStepInfo?.approver_mode === 'specific_user' && !nextApproverId) {
      toast.show('Please select the specific approver for the next step.', 'error');
      return;
    }
    setActing(true);
    try {
      const payload = { action: actionType, comment: comment || null };
      if (actionType === 'approve' && nextStepInfo?.approver_mode === 'specific_user') {
        payload.next_approver_id = nextApproverId;
      }
      await api.actOnWorkflow(voucher.workflow_instance_id, payload);
      onActed && onActed();
      setShowActionModal(false);
      setOutcome({
        type: actionType,
        title: `Voucher ${actionType === 'approve' ? 'Approved' : actionType === 'reject' ? 'Rejected' : actionType === 'return' ? 'Returned' : 'Put On Hold'}`,
        message: OUTCOME_MESSAGES[actionType],
      });
    } catch (e) {
      setShowActionModal(false);
      setOutcome({ type: 'error', title: 'Action Failed', message: parsePvErrorMessage(e.message).header });
    } finally {
      setActing(false);
    }
  };

  const submitPost = async () => {
    setPosting(true);
    try {
      await api.releasePaymentVoucher(voucher.payment_voucher_id, {});
      setShowPostConfirm(false);
      onActed && onActed();
      setOutcome({ type: 'release', title: 'Voucher Released', message: 'The voucher has been released into the Expense Register.' });
    } catch (e) {
      setShowPostConfirm(false);
      setOutcome({ type: 'error', title: 'Release Failed', message: parsePvErrorMessage(e.message).header });
    } finally {
      setPosting(false);
    }
  };

  // When the current step is pinned to a specific person, only that person
  // should see the action buttons — a same-role user would otherwise be
  // able to click Approve/Reject/etc. only to have the backend reject it
  // with "Not authorized to act on this step."
  const handleResubmit = async () => {
    setResubmitting(true);
    try {
      await api.resubmitWorkflow(voucher.workflow_instance_id, resubmitComment || null);
      toast.show(`Voucher ${voucher.voucher_no} resubmitted for approval.`, 'success');
      setShowResubmit(false);
      setResubmitComment('');
      onActed && onActed();
      onBack && onBack();
    } catch (e) {
      toast.show(e.message, 'error');
    } finally {
      setResubmitting(false);
    }
  };

  const assignedTo = tracking?.current_step?.assigned_to;

  // ── Returned vouchers ─────────────────────────────────────────
  const isReturned = voucher?.status === 'returned';
  // The engine surfaces the returning step's comment as return_reason; fall
  // back to the last returned entry in the trail if it is not set.
  const returnReason = tracking?.return_reason
    || [...(tracking?.approval_history || [])].reverse()
        .find(h => h.status === 'returned')?.comments
    || null;
  // Only the person who raised it can correct and resend it.
  const canOwnerAct = !currentUser?.user_id
    || !voucher?.created_by_id
    || currentUser.user_id === voucher.created_by_id;
  const isAssignedToMe = !assignedTo || assignedTo.id === currentUser?.user_id;

  if (loading || !voucher) {
    return (
      <div style={{ padding: '40px 20px', display: 'flex', justifyContent: 'center' }}>
        <RNLLoader title="Loading voucher…" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{`
        @keyframes hourglass-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .hourglass-pending { animation: hourglass-spin 2s linear infinite; transform-origin: center; }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={onBack}
            style={{
              padding: '6px 16px', background: '#e0f2fe', color: '#1e3a8a', border: '2px solid #93c5fd',
              borderRadius: 9999, cursor: 'pointer', fontWeight: 700, fontSize: 14,
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
          <h2 style={{ margin: 0, fontSize: 20 }}>
            Payment Voucher: {voucher.voucher_no || 'Not yet submitted'}
          </h2>
        </div>

        {showWorkflowActions && voucher.workflow_instance_id && !isAssignedToMe && (
          <div style={{ fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
            View only — this step is assigned to {assignedTo?.full_name || 'another approver'}.
          </div>
        )}

        {showWorkflowActions && voucher.workflow_instance_id && isAssignedToMe && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={() => openActionModal('reject')}
              disabled={!!preparing || acting}
              title="Reject this voucher permanently"
              style={{
                padding: '8px 20px', background: '#ef4444', color: '#fff', border: '2px solid #ef4444',
                borderRadius: 9999, fontWeight: 700, fontSize: 13,
                cursor: (preparing || acting) ? 'not-allowed' : 'pointer',
                opacity: (preparing || acting) ? 0.6 : 1,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              REJECT VOUCHER
            </button>
            <button
              onClick={() => openActionModal('hold')}
              disabled={!!preparing || acting}
              title="Put this voucher on hold"
              style={{
                padding: '8px 20px', background: '#eab308', color: '#fff', border: '2px solid #eab308',
                borderRadius: 9999, fontWeight: 700, fontSize: 13,
                cursor: (preparing || acting) ? 'not-allowed' : 'pointer',
                opacity: (preparing || acting) ? 0.6 : 1,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
              </svg>
              HOLD VOUCHER
            </button>
            <button
              onClick={() => openActionModal('return')}
              disabled={!!preparing || acting}
              title="Return this voucher to the requester"
              style={{
                padding: '8px 20px', background: '#fff', color: '#f97316', border: '2px solid #f97316',
                borderRadius: 9999, fontWeight: 700, fontSize: 13,
                cursor: (preparing || acting) ? 'not-allowed' : 'pointer',
                opacity: (preparing || acting) ? 0.6 : 1,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" />
              </svg>
              RETURN VOUCHER
            </button>
            <button
              onClick={() => openActionModal('approve')}
              disabled={!!preparing || acting}
              title="Approve this voucher"
              style={{
                padding: '8px 20px', background: '#16a34a', color: '#fff', border: '2px solid #16a34a',
                borderRadius: 9999, fontWeight: 700, fontSize: 13,
                cursor: (preparing || acting) ? 'not-allowed' : 'pointer',
                opacity: (preparing || acting) ? 0.6 : 1,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {preparing === 'approve'
                ? <Spinner size={15} />
                : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                )}
              {preparing === 'approve' ? 'PREPARING…' : 'APPROVE VOUCHER'}
            </button>
          </div>
        )}

        {showPostAction && voucher.status === 'approved' && (
          <button
            onClick={() => setShowPostConfirm(true)}
            title="Post this voucher"
            style={{
              padding: '8px 20px', background: '#0b318b', color: '#fff', border: '2px solid #0b318b',
              borderRadius: 9999, fontWeight: 700, fontSize: 13, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            POST VOUCHER
          </button>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
          {/* Derived label, not the raw workflow status — a released voucher is
              still "approved" underneath and read as APPROVED here. */}
          <Badge
            color={voucher.review_status ? reviewStatusColor(voucher.review_status) : pvStatusColor(voucher.status)}
            pulse={voucher.review_status === 'Awaiting Review'}
            style={{ textTransform: 'uppercase', fontSize: 13, padding: '5px 14px' }}
          >
            {voucher.review_status || pvStatusLabel(voucher.status)}
          </Badge>

          {/* The reason sits beside the status, not buried at the end of the
              approval rail — it is the one thing the owner must read before
              they can act on a returned voucher. */}
          {isReturned && returnReason && (
            <div style={{ fontSize: 12.5, fontStyle: 'italic', color: '#c2410c', fontWeight: 700 }}>
              “{returnReason}”
            </div>
          )}
        </div>

        {isReturned && canOwnerAct && (
          <div style={{ display: 'flex', gap: 8 }}>
            {/* One route, not two. Editing a returned voucher and resubmitting
                it are the same task, and the plain form's Save Draft / Submit
                footer does not apply to a voucher that is already in a chain. */}
            <Btn size="sm" onClick={() => (onResubmit ? onResubmit(voucher) : setShowResubmit(true))}>
              Correct &amp; Resubmit
            </Btn>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, padding: 14, background: '#f8fafc', borderRadius: 10, fontSize: 12.5, border: '1px solid black' }}>
        <Field label="Voucher Date" value={fmtDate(voucher.voucher_date)} />
        {/* The stored value is an enum ("supplier_payment"); show its label. */}
        <Field label="Payment Type" value={
          (PAYMENT_TYPE_OPTIONS.find(o => o.value === voucher.payment_type) || {}).label || voucher.payment_type
        } />
        <Field label="Supplier" value={voucher.supplier_name || '—'} />
        <Field label="Currency" value={voucher.currency} />
        <Field label="Invoice No." value={voucher.invoice_no || '—'} />
        <Field label="Requisition" value={voucher.purchase_request_pr_number || '—'} />
        <Field label="Created By" value={voucher.created_by_name || '—'} />
        {/* Stored lowercase ("normal", "urgent") — capitalised for display. */}
        <Field label="Priority" value={
          voucher.priority ? voucher.priority.charAt(0).toUpperCase() + voucher.priority.slice(1) : '—'
        } />
      </div>

      {/* Where this voucher came from. The strip above answers "what is this
          payment"; this answers "what was it raised against", which is the
          question anyone auditing a voucher asks next and previously had to
          leave the page to find. Hidden entirely when the voucher is not
          against a requisition — an empty card would imply one exists. */}
      {source && (
        <div style={{ border: '1px solid black', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{
            background: '#0b318b', color: '#fff', padding: '8px 14px',
            fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
          }}>
            SOURCE REQUISITION INFORMATION
          </div>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 10, padding: 14, background: '#f8fafc', fontSize: 12.5,
          }}>
            <Field label="Req Ref" value={source.pr_number || '—'} />
            <Field label="Date Raised" value={source.date_raised ? fmtDate(source.date_raised) : '—'} />
            <Field label="Requester" value={source.requested_by_name || '—'} />
            <Field label="Department" value={source.department_name || '—'} />
            <Field label="Request Type" value={source.request_type_name || '—'} />
            <Field label="Project / Site" value={source.project_name || '—'} />
            <Field label="Priority" value={
              source.priority_status
                ? source.priority_status.charAt(0).toUpperCase() + source.priority_status.slice(1)
                : '—'
            } />
            <Field label="Due Date" value={source.expected_due_date ? fmtDate(source.expected_due_date) : '—'} />
            <Field label="Approved By" value={source.approved_by_name || '—'} />
            <Field label="Approved On" value={source.approved_at ? fmtDate(source.approved_at) : '—'} />
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid black', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#0b318b', color: '#fff' }}>
              {['#', 'Description', 'Unit', 'Qty', 'Rate', 'Amount'].map(h => (
                <th key={h} style={{
                  padding: '7px 10px', textAlign: 'center', fontSize: 10.5, fontWeight: 800,
                  borderRight: '1px solid rgba(255,255,255,0.28)',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {voucher.items.map((it, idx) => (
              // Every row rules off, including the last, so the block closes
              // against the card border instead of stopping short.
              <tr key={it.item_id} style={{ borderBottom: '1px solid #cbd5e1' }}>
                <td style={{ ...pTd, textAlign: 'center' }}>{idx + 1}</td>
                <td style={{ ...pTd, textAlign: 'center' }}>{it.description}</td>
                <td style={{ ...pTd, textAlign: 'center' }}>{it.unit_of_measure || '—'}</td>
                <td style={{ ...pTd, textAlign: 'center' }}>{it.process_quantity}</td>
                <td style={{ ...pTd, textAlign: 'center' }}>{fmt(it.unit_rate)}</td>
                <td style={{ ...pTd, textAlign: 'center', fontWeight: 700 }}>{fmt(it.line_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, padding: 14, background: '#f8fafc', borderRadius: 10, border: '1px solid black' }}>
        <Field label="Subtotal" value={fmt(voucher.subtotal_amount)} />
        <Field label="Gross" value={fmt(voucher.gross_amount)} />
        <Field label={`VAT (${voucher.vat_percent}%)`} value={fmt(voucher.vat_amount)} />
        <Field label={`WHT (${voucher.wht_percent}%)`} value={`-${fmt(voucher.wht_amount)}`} />
        <Field label="Net Payable" value={fmt(voucher.net_payable)} big />
      </div>

      {tracking && (() => {
      // See PRDetailsView for the full reasoning: history entries are not
      // guaranteed to precede the current step once return_to_previous is used.
      const timelineNodes = [
        ...(tracking.approval_history || []).map((hist, idx) => ({
          kind: 'history', key: `hist-${hist.step_id || idx}`, order: hist.step_order ?? idx + 1, hist,
        })),
        ...(tracking.current_step
          ? [{ kind: 'current', key: 'current', order: tracking.current_step.step_order ?? Number.MAX_SAFE_INTEGER }]
          : []),
      ].sort((a, b) => a.order - b.order);
      return (
        <div style={{ background: '#fff', color: '#0f172a', padding: '12px 20px', borderRadius: 8, border: '1px solid black' }}>
          <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 1, color: '#64748b', marginBottom: 10 }}>
            <svg width="13" height="13" style={{ marginRight: 5, verticalAlign: 'middle' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
            APPROVAL PROGRESS &amp; HISTORY
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', overflowX: 'auto' }}>
            {/* Requester Step */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 150 }}>
              <div style={{ zIndex: 1, width: 40, height: 40, borderRadius: '50%', border: '1.5px solid #10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#fff', border: '2px solid #10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
              </div>
              {tracking.requester_details?.profile_pic && (
                <img src={tracking.requester_details.profile_pic} alt="profile" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', marginTop: 6 }} />
              )}
              <div style={{ fontWeight: 600, fontSize: 13, marginTop: 6 }}>Submitted By</div>
              <div style={{ color: '#475569', fontSize: 12 }}>{tracking.requester_details?.full_name}</div>
            </div>

            {/* Ordered by STEP ORDER, not "history then current" — a voucher
                sent back by return_to_previous sits BEHIND the step that
                returned it, so concatenating drew them the wrong way round. */}
            {timelineNodes.map((node) => {
              if (node.kind === 'history') {
                const hist = node.hist;
                const HIST_STYLE = {
                  approved: { color: '#10b981', icon: <polyline points="20 6 9 17 4 12" /> },
                  returned: { color: '#f97316', icon: (<><polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" /></>) },
                  rejected: { color: '#ef4444', icon: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>) },
                };
                const style = HIST_STYLE[hist.status] || HIST_STYLE.approved;
                const outcomeLabel = hist.status === 'returned' ? 'Returned' : hist.status === 'rejected' ? 'Rejected' : null;
                return (
                <React.Fragment key={node.key}>
                  <div style={{ flex: '1 1 80px', minWidth: 80, height: 2, background: style.color, marginTop: 19 }} />
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 150, maxWidth: 200 }}>
                    <div style={{ zIndex: 1, width: 40, height: 40, borderRadius: '50%', border: `1.5px solid ${style.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#fff', border: `2px solid ${style.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: style.color }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">{style.icon}</svg>
                      </div>
                    </div>
                    {hist.approver_profile_pic && (
                      <img src={hist.approver_profile_pic} alt="profile" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', marginTop: 6 }} />
                    )}
                    <div style={{ fontWeight: 600, fontSize: 13, marginTop: 6 }}>
                      {hist.step_name}{outcomeLabel ? ` (${outcomeLabel})` : ''}
                    </div>
                    <div style={{ color: '#475569', fontSize: 12 }}>{hist.approver_fullname}</div>
                    <div style={{ color: '#64748b', fontSize: 10 }}>{hist.date_actioned ? new Date(hist.date_actioned).toLocaleString() : ''}</div>
                    {hist.comments && <div style={{ marginTop: 3, fontStyle: 'italic', color: '#475569', fontSize: 11 }}>"{hist.comments}"</div>}
                  </div>
                </React.Fragment>
              );
              }

              const onHold = tracking.current_step.is_on_hold;
              const stepColor = onHold ? '#f97316' : '#eab308';
              return (
                <React.Fragment key={node.key}>
                  <div style={{ flex: '1 1 80px', minWidth: 80, height: 2, background: '#10b981', marginTop: 19 }} />
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 150, maxWidth: 200 }}>
                    <div style={{ zIndex: 1, width: 40, height: 40, borderRadius: '50%', border: `1.5px solid ${stepColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#fff', border: `2px solid ${stepColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: stepColor }}>
                        {onHold ? (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                          </svg>
                        ) : (
                          <svg className="hourglass-pending" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 22h14" /><path d="M5 2h14" />
                            <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
                            <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
                          </svg>
                        )}
                      </div>
                    </div>
                    {tracking.current_step.assigned_to?.profile_pic && (
                      <img src={tracking.current_step.assigned_to.profile_pic} alt="profile" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', marginTop: 6 }} />
                    )}
                    <div style={{ fontWeight: 600, fontSize: 13, color: stepColor, marginTop: 6 }}>
                      {tracking.current_step.step_name} {onHold ? '(On Hold)' : '(Pending)'}
                    </div>
                    {/* Rendered only when there IS an assignee — a role-pool
                        step has nobody pinned to it, and "System / Unassigned"
                        read as though something had gone wrong. */}
                    {tracking.current_step.assigned_to?.full_name && (
                      <div style={{ color: '#475569', fontSize: 12 }}>{tracking.current_step.assigned_to.full_name}</div>
                    )}
                    {onHold && tracking.current_step.hold_comment && (
                      <div style={{ marginTop: 3, fontStyle: 'italic', color: '#9a3412', fontSize: 11 }}>"{tracking.current_step.hold_comment}"</div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}

            {/* Next Step */}
            {tracking.next_step && (
              <>
                <div style={{ flex: '1 1 80px', minWidth: 80, height: 2, background: '#eab308', borderTop: '2px dashed #eab308', marginTop: 19 }} />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 150, maxWidth: 220, opacity: 0.75 }}>
                  <div style={{ zIndex: 1, width: 40, height: 40, borderRadius: '50%', border: '1.5px dashed #94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#fff', border: '2px dashed #94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#64748b', marginTop: 6 }}>{tracking.next_step.step_name} (Next)</div>
                </div>
              </>
            )}
          </div>
        </div>
      );
      })()}

      {showResubmit && (
        <Modal title={`Resubmit ${voucher.voucher_no}`} onClose={() => setShowResubmit(false)} width={520}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              This voucher goes back to the step that returned it. Make sure the correction
              asked for has been applied before resending.
            </div>
            {returnReason && (
              <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10.5, fontWeight: 800, color: '#c2410c', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Returned because
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#7c2d12', marginTop: 3 }}>{returnReason}</div>
              </div>
            )}
            <Textarea
              label="Comment (optional)"
              value={resubmitComment}
              onChange={setResubmitComment}
              rows={3}
              placeholder="Describe what you corrected…"
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Btn variant="ghost" disabled={resubmitting} onClick={() => setShowResubmit(false)}>Cancel</Btn>
              <Btn loading={resubmitting} onClick={handleResubmit}>Confirm Resubmit</Btn>
            </div>
          </div>
        </Modal>
      )}

      {showActionModal && (() => {
        const meta = ACTION_META[actionType] || ACTION_META.approve;
        return (
          <Modal title={meta.title} onClose={() => !acting && setShowActionModal(false)}>
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 13, marginBottom: 16 }}>
                You are about to <strong>{meta.verb}</strong> this payment voucher.
                {actionType === 'reject' && ' This is final — the voucher will not be resubmittable.'}
                {actionType === 'hold' && ' The voucher stays with you and you can act on it again later.'}
              </p>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>
                  Comment {meta.commentRequired && <span style={{ color: 'red' }}>*</span>}
                </label>
                <Textarea value={comment} onChange={setComment} placeholder={meta.placeholder} rows={3} />
              </div>
              {actionType === 'approve' && nextStepInfo && nextStepInfo.approver_mode !== 'is_final' && nextStepInfo.step_name && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontSize: 13, fontWeight: 600 }}>Next Approval Step — {nextStepInfo.step_name}</label>
                  {nextStepInfo.approver_mode === 'specific_user' ? (
                    <Select
                      value={nextApproverId}
                      onChange={setNextApproverId}
                      options={nextStepInfo.eligible_users.map((a) => ({ value: a.user_id || a.id, label: `${a.name} (${a.email})` }))}
                    />
                  ) : (
                    <Select
                      value="step_queue"
                      onChange={() => {}}
                      options={[{ value: 'step_queue', label: `🏢 ${nextStepInfo.step_name}` }]}
                    />
                  )}
                </div>
              )}
              {actionType === 'approve' && nextStepInfo && nextStepInfo.approver_mode === 'is_final' && (
                <div style={{ marginBottom: 16, fontSize: 12.5, color: '#16a34a', fontWeight: 600 }}>
                  This is the final approval step — the voucher will be fully approved and ready to post.
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <Btn variant="ghost" onClick={() => setShowActionModal(false)} disabled={acting}>Cancel</Btn>
              <Btn
                onClick={submitAction}
                loading={acting}
                // Guarded rather than relying on timing: a specific-user next
                // step cannot be confirmed until that person is chosen.
                disabled={
                  acting
                  || (actionType === 'approve'
                      && nextStepInfo?.approver_mode === 'specific_user'
                      && !nextApproverId)
                }
                style={{ background: meta.color, color: '#fff', border: 'none' }}
              >
                {acting ? 'Submitting...' : `Confirm ${meta.label}`}
              </Btn>
            </div>
          </Modal>
        );
      })()}

      {showPostConfirm && (
        <Modal title="Post Voucher" onClose={() => !posting && setShowPostConfirm(false)} width={620}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>
              Posting finalizes this voucher — it will move to the posted voucher register and can no longer be edited, withdrawn, or returned. Please review the details below before confirming.
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 14, padding: 16, background: '#f8fafc', borderRadius: 10, border: '1px solid black' }}>
              <Field label="Voucher No." value={voucher.voucher_no || '—'} />
              <Field label="Voucher Date" value={fmtDate(voucher.voucher_date)} />
              <Field label="Supplier" value={voucher.supplier_name || '—'} />
              <Field label="Requisition" value={voucher.purchase_request_pr_number || '—'} />
              <Field label="Status" value={
                <Badge
                  color={voucher.review_status ? reviewStatusColor(voucher.review_status) : pvStatusColor(voucher.status)}
                  style={{ textTransform: 'uppercase', fontSize: 12.5, padding: '4px 12px' }}
                >{voucher.review_status || pvStatusLabel(voucher.status)}</Badge>
              } />
              <Field label="Net Payable" value={fmt(voucher.net_payable)} big />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Btn variant="ghost" onClick={() => setShowPostConfirm(false)} disabled={posting}>Cancel</Btn>
              <Btn
                onClick={submitPost}
                disabled={posting}
                style={{ background: '#0b318b', color: '#fff', border: 'none' }}
              >
                {posting ? 'Posting...' : 'Confirm Post'}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {outcome && <VoucherOutcomeModal outcome={outcome} onClose={() => { setOutcome(null); onBack(); }} />}
    </div>
  );
}

// borderRight on every cell gives the vertical rules; the row's own
// borderBottom gives the horizontal ones. The last column's right edge is
// closed by the wrapper's border, so no :last-child handling is needed.
const pTd = { padding: '6px 10px', borderRight: '1px solid #cbd5e1' };

function Field({ label, value, big }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: big ? 18 : 13, fontWeight: big ? 800 : 600, color: '#0f172a', marginTop: 2 }}>{value}</div>
    </div>
  );
}
